import { Component, Input, OnChanges, OnDestroy, OnInit, SimpleChanges } from '@angular/core';
import { QepFileBrowserService } from '@app/modules/documents/services/qep-file-browser.service';
import { IAttachment } from '@common/models/IAttachment';
import { ConfirmationService } from 'primeng/api';
import { ApprovalRequestType } from '@common/enums/ApprovalRequestType';
import { IFundFamily } from '@common/models/IFundFamily';
import { IPortfolioCompany } from '@common/models/IPortfolioCompany';
import { firstValueFrom, forkJoin, from, Observable, Subscription } from 'rxjs';
import { FundFamilyService } from '@app/modules/companies/services/fund-family.service';
import { IStorageStat } from '@common/models/IStorageStat';
import { FileBrowserRawData, QEPFileUploadEvent, IDealName } from 'qep-file-browser/lib/qep-file-browser.interface';
import { pdfTitleChange } from '@app/services/common/utils';
import { FilePreviewService, MIME_TYPES } from '@app/services/common/file-preview.service';
import { QuangularToasterService } from '@app/services/common/toaster.service';
import { SystemPropertiesService } from '@app/core/services/system-properties.service';
import { IConfig } from '@common/models/IConfig';
import { SystemPropertyKey } from '@common/enums/SystemPropertyKey';
import { REGEX } from '@common/constants/regex.constant';
import { BmAttachmentType } from '@common/enums/boardMeeting/AttachmentType';
import { InvestmentActivityFileType } from '@common/enums/InvestmentActivity';
import { IFundCapitalMember } from '@common/models/ICapitalMember';
import { FundCapitalMembersService } from '@app/modules/admin/services/fund-capital-members.service';
import { ConfigMetrics } from '@common/enums/ConfigMetrics';
import { ValuationService } from '@app/modules/companies/services/valuation.service';
import { ConfigService } from '@app/services/config.service';
import { IValuation } from '@common/models/IValuation';

const IC_MEMO_NEW_DEAL_SEGMENT = 'IC_Memo_New_Deal';

@Component({
  selector: 'app-document-repository',
  templateUrl: './document-repository.component.html',
  styleUrls: ['./document-repository.component.scss'],
})
export class DocumentRepositoryComponent implements OnInit, OnChanges, OnDestroy {

  @Input() fundsRequired = true;
  @Input() rawFiles: any[];
  @Input() showCompany = false;
  @Input() mnemonic: string | null = null;

  funds: IFundFamily[] = [];
  dealNames: IDealName[] = [];
  portfolioCompanies: IPortfolioCompany[] = [];
  responseReceived = false;
  breadcrumbVisible = false;

  /** Unique IC Memo New Deal folder names — drives the deal-name dropdown in the upload dialog */
  existingDealNames: string[] = [];
  isVersionHistoryVisible = false;

  finalDealNamesForDropdown: string[] = [];

  dealNamesForBrowser: IDealName[] = [];

  versionHistory?: {
    value: IStorageStat[];
    attachment: any;
    event: FileBrowserRawData;
  };

  configData: IConfig = null;
  fundCapitalMembers: IFundCapitalMember[] = [];

  configs: IConfig[] = [];

  private subscription: Subscription = new Subscription();

  constructor(
    private readonly qepFileBrowserService: QepFileBrowserService,
    private readonly filePreview: FilePreviewService,
    private readonly confirmationService: ConfirmationService,
    private readonly fundFamilyService: FundFamilyService,
    private readonly qToaster: QuangularToasterService,
    private readonly systemPropertyService: SystemPropertiesService,
    private readonly fundCapitalMemberService: FundCapitalMembersService,
    private readonly configService: ConfigService, 
    private readonly valuationService: ValuationService
  ) { 
    this.getConfigData();
    this.subscription.add(this.fundCapitalMemberService.get().subscribe((res)=>{
      this.fundCapitalMembers = res;
    }));
  }

  // ================================
  // LIFECYCLE
  // ================================

  ngOnInit(): void {
    this.sortFilesByQuarter();

    const apiCalls: Observable<any>[] = [
      this.qepFileBrowserService.getAllPorfolioCompanies(),
      from(this.getDealNames())
    ];

    if (this.fundsRequired) {
      apiCalls.push(this.fundFamilyService.getAll());
    }

    this.subscription.add(forkJoin(apiCalls).subscribe(([pcs, dealNames, funds]) => {
      this.portfolioCompanies = pcs;
      this.dealNames = dealNames || [];
      this.funds = funds || [];
      this.responseReceived = true;
      this.extractExistingDealNames();
      this.buildFinalDealNames();
    }));
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['rawFiles']) {
      this.extractExistingDealNames();
    }

    if (changes['mnemonic']) {
      if (this.dealNames?.length || this.responseReceived) {
        this.buildFinalDealNames();
      }
    }
  }

  ngOnDestroy(): void {
    this.subscription.unsubscribe();
  }


  // ─── Public methods ────────────────────────────────────────────────────────

  copyLink(ev: FileBrowserRawData): void {
    const url = `${globalThis.location.origin}/documents/download?path=${encodeURIComponent(ev.path)}&fileName=${encodeURIComponent(ev.name)}`;
    navigator.clipboard.writeText(url).then(() => {
      this.qToaster.showToast({ message: 'Document link copied to clipboard.', status: 'success' });
    }).catch(() => {
      this.qToaster.showToast({ message: 'Failed to copy link to clipboard.', status: 'error' });
    });
  }

  deleteFile(ev: any): void {
    this.confirmationService.confirm({
      message: 'This will delete the selected file. Are you sure that you want to proceed?',
      header: 'Confirmation',
      icon: 'pi pi-exclamation-triangle',
      accept: () => {

        let delete$: Observable<any>;

        if (ev.investmentActivity === InvestmentActivityFileType.IC_Memo) {
          const name = ev.name.split('_')[1] ?? '';
          delete$ = this.qepFileBrowserService.deleteIcMemoFile(
            ev.mnemonic,
            ev.activityName,
            name,
            ev.documentType,
            ev.name
          );

        } else if (ev.investmentActivity === InvestmentActivityFileType.Technical_Materials) {
          const quarter = ev.name.split('_')[1] ?? '';
          const name = ev.name.split('_')[2] ?? '';
          delete$ = this.qepFileBrowserService.deleteTechnicalMaterialsFile(
            ev.mnemonic,
            quarter,
            name,
            ev.documentType,
            ev.name
          );
        } else if (ev.investmentActivity === InvestmentActivityFileType.Legal) {
          const name = ev.name.split('_')[1] ?? '';
          delete$ = this.qepFileBrowserService.deleteLegalFile(
            ev.mnemonic,
            ev.activityName,
            name,
            ev.documentType,
            ev.name
          );
        } else {
          const date = ev.investmentActivity === ApprovalRequestType.Valuation
            ? null
            : ev.name.split('_')[2];

          delete$ = this.qepFileBrowserService.deleteFile(
            ev.mnemonic,
            ev.activityName,
            date,
            ev.investmentActivity,
            ev.documentType,
            0,
            ev.name
          );
        }

        this.subscription.add(delete$.subscribe({
          next: d => {
            this.qToaster.showToast({ message: d.message, status: 'success' });
            this.rawFiles = this.rawFiles.filter(
              rf => !(rf.storageStat.name === ev.name && rf.storageStat.path === ev.path)
            );
            this.extractExistingDealNames();
          },
          error: (error: any) => {
            this.qToaster.showToast({ message: error.message, status: 'error' });
          }
        }));
      },
      reject: () => { }
    });
  }

  downloadFile(ev: FileBrowserRawData): void {
    this.subscription.add(this.qepFileBrowserService.downloadFile(ev.name, ev.path).subscribe({
      next: data => {
        const file = new Blob([data], { type: 'application/octet-stream' });
        const fileURL = URL.createObjectURL(file);
        const link = document.createElement('a');
        link.href = fileURL;
        link.target = '_blank';
        link.download = ev.name;
        link.click();
        URL.revokeObjectURL(fileURL);
        setTimeout(() => {
          this.qToaster.showToast({ message: `File ${ev.name} downloaded successfully.`, status: 'success' });
        }, 100);
      },
      error: () => {
        this.qToaster.showToast({ message: `File ${ev.name} could not be downloaded.`, status: 'error' });
      }
    }));
  }

  downloadFiles(ev: FileBrowserRawData): void {
    this.subscription.add(this.qepFileBrowserService.downloadFiles(ev.downloadPath.substring(1), ev.mnemonic).subscribe({
      next: data => {
        const file = new Blob([data], { type: 'application/zip' });
        const fileURL = URL.createObjectURL(file);
        const link = document.createElement('a');
        link.href = fileURL;
        link.target = '_blank';
        link.download = 'documents.zip';
        link.click();
        URL.revokeObjectURL(fileURL);
        setTimeout(() => {
          this.qToaster.showToast({ message: 'Files downloaded successfully.', status: 'success' });
        }, 100);
      },
      error: () => {
        this.qToaster.showToast({ message: 'Files could not be downloaded.', status: 'error' });
      }
    }));
  }

  uploadFile(ev: QEPFileUploadEvent): void {
    // investmentActivity is IC_Memo set activityName to icMemoCategory
    if ((ev?.rawData?.investmentActivity as string) === InvestmentActivityFileType.IC_Memo && ev?.rawData?.icMemoCategory) {
      ev.rawData.activityName = ev?.rawData?.icMemoCategory;
    } else if((ev?.rawData?.investmentActivity as string) === InvestmentActivityFileType.Legal) {
      ev.rawData.activityName = ev?.rawData?.documentType;
    }
    const updatedEvent: QEPFileUploadEvent = {
      ...ev,
      rawData: {
        ...ev.rawData,
        dealNames: this.finalDealNamesForDropdown,
        existingDealNames: this.finalDealNamesForDropdown
      }
    };

    if (ev.originalFile) {
      const fileCount = (ev.files || []).length;
      const message = fileCount > 1
        ? `This will replace ${fileCount} existing documents. Are you sure that you want to proceed?`
        : `This will replace existing document ${ev.originalFile}. Are you sure that you want to proceed?`;

      this.confirmationService.confirm({
        message,
        header: 'Confirmation',
        icon: 'pi pi-exclamation-triangle',
        accept: () => this.completeUpload(updatedEvent),
        reject: () => { }
      });

    } else {
      this.completeUpload(updatedEvent);
    }
  }

  viewFile(ev: FileBrowserRawData): void {
    const ext = ev.name.split('.').pop()!.toLowerCase();
    this.filePreview.openAttachment({
      fileName: ev.name,
      path: ev.path,
      mimeType: MIME_TYPES[ext],
      index: 0,
      attachmentType: ev.documentType
    });
  }

  viewVersionHistory(ev: FileBrowserRawData): void {
    const ext = ev.name.split('.').pop()!.toLowerCase();
    const attachment: IAttachment = {
      fileName: ev.name,
      path: ev.path,
      mimeType: MIME_TYPES[ext],
      index: 0,
      attachmentType: ev.documentType,
      lastModifiedBy: ev.lastModifiedBy
    };
    this.subscription.add(this.qepFileBrowserService
      .getVersionAttachmentList(ev.mnemonic, ev.investmentActivity, ev.name, ev.activityName, this.getDate(ev))
      .subscribe(d => {
        this.versionHistory = { value: d, event: ev, attachment };
        this.isVersionHistoryVisible = true;
      }));
  }

  closeVersionHistory(): void {
    this.isVersionHistoryVisible = false;
    this.versionHistory = undefined;
  }

  downloadVersionTemplate(event: any): void {
    const { attachment, version } = event;
    const { mnemonic, investmentActivity, activityName } = this.versionHistory!.event;
    this.subscription.add(this.qepFileBrowserService
      .downloadAttachmentVersion(mnemonic, investmentActivity, activityName, attachment, version, this.getDate(this.versionHistory!.event))
      .subscribe({
        next: async (blob: Blob) => {
          const fileURL = URL.createObjectURL(blob);
          const newBlob = await pdfTitleChange(fileURL, attachment.fileName, attachment.mimeType);
          const newFileURL = URL.createObjectURL(newBlob);
          window.open(newFileURL, 'attachment_version_history', 'width=800,height=1024,popup=yes,scrollbars=yes,resizable=yes');
        },
        error: () => {}
      }));
  }

  toggleBreadCrumb(isShowing: boolean): void {
    this.breadcrumbVisible = isShowing;
  }

  get filteredDealNamesAsStrings(): string[] {
    if (!this.dealNames?.length) return [];

    return this.dealNames
      .filter(d => d.enabled !== false)
      .filter(d => {
        if (!d.mnemonic) return true;
        if (!this.mnemonic) return true;
        return d.mnemonic === this.mnemonic;
      })
      .map(d => d.dealName)
      .filter(Boolean);
  }

  get filteredDealNames(): IDealName[] {
    if (!this.dealNames?.length) return [];
    return this.dealNames.filter(d => d.enabled !== false);
  }

 
  private extractExistingDealNames(): void {
    if (!this.rawFiles?.length) {
      this.existingDealNames = [];
      if (this.responseReceived) {
        this.buildFinalDealNames();
      }
      return;
    }

    const dealNameSet = new Set<string>();

    this.rawFiles.forEach((rf: any) => {
      const props = rf.properties ?? {};
      const path: string = rf.storageStat?.path ?? '';

      // Filter by mnemonic only when mnemonic is set
      if (this.mnemonic && props.mnemonic && props.mnemonic !== this.mnemonic) return;

      if (!props.investmentActivity?.toLowerCase().includes('ic')) return;

      const isNewDeal =
        props.icMemoCategory === IC_MEMO_NEW_DEAL_SEGMENT ||
        path.includes(`/${IC_MEMO_NEW_DEAL_SEGMENT}/`);

      if (!isNewDeal) return;

      if (props.icMemoDeal?.trim()) {
        dealNameSet.add(props.icMemoDeal.trim());
        return;
      }

      const parts = path.replace(REGEX.STARTS_WITH_SLASH, '').split('/');
      if (parts[3]?.trim() && !parts[3].match(REGEX.FILE_EXTENSION_REGEX)) {
        dealNameSet.add(parts[3].trim());
      }
    });

    this.existingDealNames = Array.from(dealNameSet)
      .sort((a, b) => a.localeCompare(b));

    if (this.responseReceived) {
      this.buildFinalDealNames();
    }

  }

  /**
   * Merges config deal names (IDealName[] from API) with DB-sourced names
   * (extracted from rawFiles paths/properties).
   *
   * Produces:
   *   dealNamesForBrowser  — IDealName[] bound to [dealNames] on q-file-browser
   *   finalDealNamesForDropdown — string[] bound to [existingDealNames] and rawData.dealNames
   */
  private async getDealNames(): Promise<IDealName[]> {
    const configDoc = await firstValueFrom(
      this.systemPropertyService.getByKey<any>(SystemPropertyKey.configs)
    );

    const dealNamesConfig = configDoc?.find(
      (c: any) => c.config_name === 'qep_file_browser_deal_names'
    );

    const rawValues: any[] = Array.isArray(dealNamesConfig?.config_value)
      ? dealNamesConfig.config_value
      : [];

    // ✅ No enabled filter here — keep all entries, filtering happens in buildFinalDealNames
    const mapped: IDealName[] = rawValues.map((entry: any) => ({
      dealName: typeof entry === 'string'
        ? entry
        : (entry.dealName ?? entry.name ?? entry.label ?? ''),
      mnemonic: entry.mnemonic ?? null,
      enabled: entry.enabled ?? true
    })).filter(d => !!d.dealName); 


    this.configs = rawValues as IConfig[];
    return mapped;
  }

  private buildFinalDealNames(): void {    

    // q-file-browser.getConfigDealNamesForMnemonic() will filter by row mnemonic
    const configEntries: IDealName[] = this.dealNames.map(d => ({
      dealName: d.dealName,
      mnemonic: d.mnemonic ?? '',  
      enabled: d.enabled ?? true
    }));

    const dbEntries: IDealName[] = [];
    (this.rawFiles || []).forEach((rf: any) => {
      const props = rf.properties ?? {};
      const path: string = rf.storageStat?.path ?? '';
      const fileMnemonic: string = props.mnemonic ?? '';

      if (!props.investmentActivity?.toLowerCase().includes('ic')) return;

      const isNewDeal =
        props.icMemoCategory === IC_MEMO_NEW_DEAL_SEGMENT ||
        path.includes(`/${IC_MEMO_NEW_DEAL_SEGMENT}/`);

      if (!isNewDeal) return;

      let dealName = '';
      if (props.icMemoDeal?.trim()) {
        dealName = props.icMemoDeal.trim();
      } else {
        const parts = path.replace(REGEX.STARTS_WITH_SLASH, '').split('/');
        if (parts[3]?.trim() && !parts[3].match(REGEX.FILE_EXTENSION_REGEX)) {
          dealName = parts[3].trim();
        }
      }

      if (dealName && fileMnemonic) {
        dbEntries.push({
          dealName,
          mnemonic: fileMnemonic, 
          enabled: true
        });
      }
    });

    // Deduplicate by dealName+mnemonic combination
    const map = new Map<string, IDealName>();
    [...configEntries, ...dbEntries].forEach(entry => {
      const key = `${entry.mnemonic?.toLowerCase()}__${entry.dealName?.toLowerCase()}`;
      if (entry.dealName && !map.has(key)) {
        map.set(key, entry);
      }
    });

    // IDealName[] with correct mnemonics — q-file-browser filters by row mnemonic
    this.dealNamesForBrowser = Array.from(map.values())
      .sort((a, b) => a.dealName.localeCompare(b.dealName));

    // For global string[] (used in rawData.dealNames on upload event)
    // filter by this.mnemonic if set, else keep all
    this.finalDealNamesForDropdown = this.mnemonic
      ? this.dealNamesForBrowser
        .filter(d => !d.mnemonic || d.mnemonic === this.mnemonic)
        .map(d => d.dealName)
      : this.dealNamesForBrowser.map(d => d.dealName);

  }

  private async completeUpload(ev: QEPFileUploadEvent): Promise<void> {
    const validFiles = (ev.files || []).filter(f => f instanceof File && f.size > 0);

    if (validFiles.length === 0) {
      return;
    }

    // Set index value
    let index: number = 0;
    if((ev?.rawData?.investmentActivity as string) === ApprovalRequestType.Valuation) {
      const pc: IPortfolioCompany | any = this.portfolioCompanies.find(pc => pc.mnemonic === ev.rawData.mnemonic);
      const uploadValuation: IValuation =  (pc?.valuation || []).find(pc => pc.name?.trim().toLowerCase() === ev.rawData.activityName?.trim().toLowerCase());

      index = await this.getValuationAttachment(pc, uploadValuation, ev);
    }

    const uploadPayload: QEPFileUploadEvent = {
      ...ev,
      files: validFiles,
      rawData: {
        ...ev.rawData,
        icMemoCategory: ev.rawData.icMemoCategory || undefined,
        icMemoDeal: ev.rawData.icMemoDeal || undefined
      }
    };

    this.subscription.add(this.qepFileBrowserService.uploadFile(uploadPayload, index).subscribe({
      next: (res: any) => {
        const {
          mnemonic, investmentActivity, activityName,
          documentType, icMemoCategory, icMemoDeal
        } = ev.rawData;

        const results: any[] = Array.isArray(res) ? res : [res];
        const newRawFiles = structuredClone(this.rawFiles);

        results.forEach((fileRes, i) => {
          const file = validFiles[i];
          const { fileName, path, creationDate, lastModifiedDate, createdBy, lastModifiedBy } = fileRes;

          const data = {
            storageStat: {
              name: fileName,
              path: path,
              size: file?.size || 0,
              isDirectory: false,
              isFile: true,
              birthtime: creationDate,
              mtime: lastModifiedDate,
              createdBy,
              lastModifiedBy
            },
            properties: {
              mnemonic,
              investmentActivity,
              activityName,
              documentType,
              icMemoCategory: icMemoCategory || undefined,
              icMemoDeal: icMemoDeal || undefined
            }
          };

          const existingIndex = newRawFiles.findIndex(
            (rf: any) => rf.storageStat.name === fileName
          );
          if (existingIndex >= 0) {
            newRawFiles[existingIndex] = data;
          } else {
            newRawFiles.unshift(data);
          }
        });

        this.rawFiles = newRawFiles;
        // Re-derive after upload — newly typed deal name is now in rawFiles
        this.extractExistingDealNames();

        this.qToaster.showToast({
          message: `${validFiles.length} file(s) uploaded successfully.`,
          status: 'success'
        });
      },
      error: () => {
        this.qToaster.showToast({ message: 'Upload failed.', status: 'danger' });
      }
    }));
  }

  private sortFilesByQuarter(): void {
    this.rawFiles?.sort((a, b) =>
      new Date(a.storageStat.mtime) > new Date(b.storageStat.mtime) ? -1 : 1
    );

    this.rawFiles?.sort((a, b) => {
      const quarterStrA: string = a.storageStat.path.split('/')[2] || '';
      const quarterStrB: string = b.storageStat.path.split('/')[2] || '';
      const matchA = quarterStrA.match(REGEX.YEAR_QUARTER);
      const matchB = quarterStrB.match(REGEX.YEAR_QUARTER);

      if (matchA && matchB) {
        const yearDiff = Number(matchB[1]) - Number(matchA[1]);
        return yearDiff !== 0 ? yearDiff : Number(matchB[2]) - Number(matchA[2]);
      }
      return 0;
    });
  }

  private getDate(ev: any): string {
  if (ev.investmentActivity === ApprovalRequestType.Board_Materials) {
    return ev.name.split('.')[0].split('_').pop() ?? '';
  }

  if (ev.investmentActivity === InvestmentActivityFileType.IC_Memo) {
    if (ev.path?.includes(`/${IC_MEMO_NEW_DEAL_SEGMENT}/`)) {
      const parts = ev.path.split('/');
      const index = parts.indexOf(IC_MEMO_NEW_DEAL_SEGMENT);

      if (index >= 0 && parts[index + 1]) {
        return parts[index + 1];
      }

      return '';
    }
    return ev.name.split('.')[0].split('_').pop() ?? '';
  }

  if (ev.investmentActivity === InvestmentActivityFileType.Technical_Materials) {
    const attachmentTypes = [BmAttachmentType.technicalEvalSupport];
    const matchedType = attachmentTypes.find(type => ev.path?.includes(`/${type}/`));
    if (matchedType) {
      const parts = ev.path.split('/');
      const index = parts.indexOf(matchedType);

      if (index >= 0 && parts[index + 1]) {
        return parts[index + 1];
      }

      return '';
    }
    return ev.name.split('.')[0].split('_')[2] ?? '';
  }

  if (ev.investmentActivity === InvestmentActivityFileType.Legal) {
    const attachmentTypes = [BmAttachmentType.llcAgreement, BmAttachmentType.llcAgreementAmendment];
    const matchedType = attachmentTypes.find(type => ev.path?.includes(`/${type}/`));
    if (matchedType) {
      const parts = ev.path.split('/');
      const index = parts.indexOf(matchedType);

      if (index >= 0 && parts[index + 1]) {
        return parts[index + 1];
      }

      return '';
    }
    return ev.name.split('.')[0].split('_')[2] ?? '';
  }

  return ''; 
}

  private async getValuationAttachment(company: IPortfolioCompany, valuation: IValuation, ev: QEPFileUploadEvent): Promise<number> {
    let index = 0;

    if(valuation?.attachments?.length){
      if (valuation) {
          await this.valuationService.setAttachmentTypes(valuation, company, this.fundCapitalMembers, this.configData).then(() => {}).catch(() => {});

          const uploadAttachment =  (valuation?.attachments || []).find(pc => 
              pc.attachmentType?.trim().toLowerCase() === ev.rawData.documentType?.trim().toLowerCase() &&
              pc.fundName?.trim().toLowerCase() === ev.rawData.writeUpFund?.trim().toLowerCase()
          );

          index = uploadAttachment?.index || 0;
      }
    }

    return index;
  }

  private getConfigData() {
    // Fetch config by metric
    this.subscription.add(this.configService.getConfigByMetric(ConfigMetrics.ValuationWriteupQCSConsolidated)
      .subscribe(config => {
        this.configData = config;
      }));
  }
}